import * as React from 'react';
import cx from 'classnames';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import ChatClient, { BlockStatus, TypingStatus, ab2str } from 'fk-cp-chat';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'ChatProvider';
import toastConfig from 'toasts.json';
import { getVisitorId, getShareProductObject } from 'utils/chatUtils';
import getImageUrl from 'utils/getImageUrl';
import { getRelativeTime } from 'utils/timeUtils';
import { isCompleted } from 'utils/asyncStatusHelper';
import { getObject, removeObject } from 'utils/localStorage';
import omnitureHelper from 'utils/omnitureHelper';
import fdpHelper from 'utils/FDPHelper';
import StringWidget, { stringTemplate } from 'components/StringWidget/StringWidget';
import { FileUploadProvider } from 'components/FileUpload';
import Button from 'components/Button';
import Card from 'components/Common/Card/index';
import MessageInput from 'components/MessageInput/MessageInput';
import ChatMessages from 'components/ChatMessages/ChatMessages';
import withToast from 'components/Toast/withToast';
import placeholderVisitorProfileIcon from 'images/chat/visitor-profile-icon.svg';
import noChats from 'images/chat/no-chats.svg';
import threeDots from 'images/chat/three-dots.svg';
import unblockIcon from 'images/chat/unblock-icon.svg';
import blockIcon from 'images/chat/block-icon.svg';
import SimpleImage from 'components/Common/Image/SimpleImage';
import styles from './Chat.css';
const MAX_FILE_SIZE_BYTES = 5242880; // 5 MB
const TYPING_STATUS_WAIT_TIME = 300;
var ChatStateEnum;
(function (ChatStateEnum) {
    ChatStateEnum["ACTIVE"] = "ACTIVE";
    ChatStateEnum["RESOLVED"] = "RESOLVED";
    ChatStateEnum["ENDED"] = "ENDED";
})(ChatStateEnum || (ChatStateEnum = {}));
export var ChatTypeEnum;
(function (ChatTypeEnum) {
    ChatTypeEnum["SELLER"] = "SELLER";
    ChatTypeEnum["CUSTOMER_SUPPORT"] = "CUSTOMER_SUPPORT";
    ChatTypeEnum["ASSISTANT"] = "ASSISTANT";
    ChatTypeEnum["BRAND"] = "BRAND";
})(ChatTypeEnum || (ChatTypeEnum = {}));
class Chat extends React.Component {
    constructor(props) {
        super(props);
        this.updateBlockStatus = (blockStatus, selectedChatMeta) => {
            const myVisitorId = getVisitorId();
            if (!myVisitorId) {
                return;
            }
            const otherParticipant = selectedChatMeta.participants.find(item => item.visitorId !== myVisitorId);
            this.chatClient.updateBlockStatus(otherParticipant.visitorId, blockStatus);
        };
        this.chatActions = [
            {
                type: 'block',
                icon: blockIcon,
                text: 'Block',
                hideAction: (chatMeta) => {
                    const { chatType } = chatMeta;
                    return chatType !== ChatTypeEnum.SELLER || this.isChatBlocked(chatMeta);
                },
                handleActionClick: this.updateBlockStatus.bind(this, BlockStatus.BLOCK)
            },
            {
                type: 'unblock',
                icon: unblockIcon,
                text: 'Unblock',
                hideAction: (chatMeta) => {
                    const { chatType } = chatMeta;
                    return chatType !== ChatTypeEnum.SELLER || !this.isChatBlocked(chatMeta);
                },
                handleActionClick: this.updateBlockStatus.bind(this, BlockStatus.UNBLOCK)
            }
        ];
        this.setVisibleFlag = () => {
            this.setState({
                isVisible: true
            });
        };
        this.unsetVisibleFlag = () => {
            this.setState({
                isVisible: false
            });
        };
        this.sendTypingStatus = (isTyping) => {
            const status = isTyping ? TypingStatus.STARTED : TypingStatus.STOPPED;
            this.chatClient.sendTypingStatus(this.state.selectedChat, status);
        };
        this.sendStartTypingStatus = debounce(this.sendTypingStatus.bind(this, true), TYPING_STATUS_WAIT_TIME, {
            leading: true,
            trailing: false
        });
        this.sendStopTypingStatus = debounce(this.sendTypingStatus.bind(this, false), TYPING_STATUS_WAIT_TIME, {
            leading: false,
            trailing: true
        });
        this.fetchProductSummaryAndSendProductShareWidget = (chatId, productId) => {
            this.props.fetchProductSummary([{ productId }]).then(response => {
                const product = get(response, ['0', 'data', productId, 'value']);
                product && this.chatClient.sendWidgetAction(chatId, getShareProductObject(product));
            });
        };
        this.fetchChatMessages = (chatId, since) => {
            this.chatClient.fetchChatMessages(chatId, since, 25, data => {
                this.handleChatStartError(data);
            });
        };
        this.handleChatStartError = (data, chatType, context) => {
            if (data && data.statusCode >= 400) {
                const chatMetas = get(this.props, 'allChats.chatMetas');
                const firstChatId = get(chatMetas, [0, 'chatId']);
                if (chatType === ChatTypeEnum.BRAND && context) {
                    const parsedContext = JSON.parse(context);
                    omnitureHelper.trackLink({ products: parsedContext.products }, 'BrandChat_Error');
                }
                if (firstChatId) {
                    this.handleChatSelect(firstChatId);
                }
                else {
                    this.context.router.push(`/my-chats`);
                }
            }
        };
        this.setRef = (c) => {
            this.chatMessagesContainerRef = c;
        };
        this.innerRefScrollAnchor = (c) => {
            this.scrollAnchor = c;
        };
        this.scrollToBottom = () => {
            this.scrollAnchor && this.scrollAnchor.scrollIntoView({ behavior: 'smooth' });
        };
        this.reOpenChat = chatId => {
            this.chatClient.issueChatAction(chatId, 'REOPEN');
        };
        this.renderMessageInput = (chatState, displayName, selectedChatMeta, handleAddFiles) => {
            switch (chatState) {
                case ChatStateEnum.ENDED:
                    return (<div className={cx(styles['chat-meta-message-container'], styles['chat-meta-ended-message-container'])}>
						<StringWidget className={styles['chat-meta-message']} attributes={{
                        displayName
                    }} strKey="chatInactiveMessage"/>
					</div>);
                case ChatStateEnum.RESOLVED:
                    return (<div className={styles['chat-meta-message-container']}>
						<StringWidget className={styles['chat-meta-message']} attributes={{
                        displayName
                    }} strKey="chatResolvedMessage"/>
						<Button className={styles['chat-action-btn']} primary onClick={() => {
                        this.reOpenChat(selectedChatMeta.chatId);
                    }}>
							{stringTemplate('chatStartChatAgainCta')}
						</Button>
					</div>);
                default:
                    return (<MessageInput handleAddFiles={handleAddFiles} networkStatus={this.props.networkStatus} sendStartTypingStatus={this.sendStartTypingStatus} sendStopTypingStatus={this.sendStopTypingStatus} messageSendCallback={this.scrollToBottom} selectedChat={this.state.selectedChat}/>);
            }
        };
        this.getValidSelectedFiles = (files) => {
            const validFiles = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (file.size <= MAX_FILE_SIZE_BYTES) {
                    validFiles.push(file);
                }
                else {
                    this.props.toast.show(toastConfig['maxFileSizeError']);
                }
            }
            return validFiles;
        };
        this.onFilesAdd = files => {
            files.forEach(file => {
                this.chatClient.createTempImageUploadFrame(this.state.selectedChat, file.id);
            });
        };
        this.handleFileUploadSuccess = file => {
            this.chatClient.sendImageWidgetAction(this.state.selectedChat, {
                type: 'image',
                serverPath: `https://blobio.flipkart.net/v1/sc?resourceId=${file.resourceId}`,
                resourceId: file.resourceId
            }, file.id);
        };
        this.isChatBlocked = chatMeta => {
            const myVisitorId = getVisitorId();
            const myParticipantDetails = (chatMeta.participants || []).find(item => item.visitorId === myVisitorId);
            return myParticipantDetails ? myParticipantDetails.state === 'BLOCKED' : false;
        };
        this.state = {
            selectedChat: '',
            isTyping: false,
            isVisible: document.hasFocus()
        };
        this.chatClient = ChatClient.getInstance();
    }
    componentDidMount() {
        const chatId = get(this.props, 'params.chatId');
        const contextId = get(this.props, ['location', 'query', 'contextId']);
        const chatType = get(this.props, ['location', 'query', 'chatType']);
        const chatMetas = get(this.props, 'allChats.chatMetas');
        fdpHelper.setNavigationContext({ pn: 'My Chat', pt: 'My Chat' });
        fdpHelper.trackPageView();
        let context;
        const chatStartContext = getObject('chatStartContext');
        if (chatStartContext) {
            context = JSON.stringify(chatStartContext);
        }
        if (chatType && context) {
            this.handleChatByType(chatType, contextId, context);
        }
        else if (!chatId && chatMetas.length) {
            const firstChatId = chatMetas[0].chatId;
            this.handleChatSelect(firstChatId);
        }
        else if (chatId) {
            this.handleChatSelect(chatId);
        }
        window.addEventListener('blur', this.unsetVisibleFlag);
        window.addEventListener('focus', this.setVisibleFlag);
    }
    componentWillUnmount() {
        window.removeEventListener('blur', this.unsetVisibleFlag);
        window.removeEventListener('focus', this.setVisibleFlag);
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const prevChatId = get(this.props, 'params.chatId');
        const chatId = get(nextProps, 'params.chatId');
        const prevChatMetas = get(this.props, 'allChats.chatMetas');
        const chatMetas = get(nextProps, 'allChats.chatMetas');
        if (!chatId && !prevChatMetas.length && chatMetas.length) {
            const firstChatId = chatMetas[0].chatId;
            this.handleChatSelect(firstChatId);
        }
        else if (chatId && chatId !== prevChatId) {
            this.handleChatSelect(chatId);
        }
    }
    handleChatByType(chatType, contextId, context) {
        this.chatClient.startChat(chatType, undefined, contextId, context, data => {
            if (data) {
                if (data.type === 'ACK') {
                    data.statusCode >= 400 && this.props.toast.show(toastConfig['genericError']);
                    this.handleChatStartError(data, chatType, context);
                }
                else {
                    this.chatClient.fetchAllChats(() => {
                        const { chatId } = data;
                        context && removeObject('chatStartContext');
                        this.context.router.replace(`/my-chats/${chatId}`);
                        this.handleChatSelect(chatId);
                        if (chatType === ChatTypeEnum.BRAND && context) {
                            const parsedContext = JSON.parse(context);
                            const productId = get(parsedContext, 'features.pid');
                            productId && this.fetchProductSummaryAndSendProductShareWidget(chatId, productId);
                        }
                    });
                }
            }
        });
    }
    handleChatSelect(chatId) {
        this.context.router.replace({
            pathname: `/my-chats/${chatId}`
        });
        this.setState({
            selectedChat: chatId
        });
    }
    handleChatActionClick(actionType) {
        return actionType;
    }
    render() {
        // Import types from fk-cp-chat library and use here. Using any for now.
        const chatMetas = get(this.props, 'allChats.chatMetas');
        const latestChatMessageIdentifiers = get(this.props, 'allChats.messageSummary.latestChatMessageIdentifiers');
        const lastReadMessageIdentifiers = get(this.props, 'allChats.messageSummary.lastReadMessageIdentifiers');
        const noChatsFound = isCompleted(this.props.allChats) && chatMetas.length === 0;
        const selectedChatMeta = chatMetas.find(chatMeta => chatMeta.chatId === this.state.selectedChat) || {};
        const isChatBlocked = this.isChatBlocked(selectedChatMeta);
        const { chatState } = selectedChatMeta;
        const { displayName, imageUrl } = selectedChatMeta;
        const myVisitorId = getVisitorId();
        const otherParticipants = (myVisitorId &&
            (selectedChatMeta.participants || []).filter(item => {
                return item.visitorId !== myVisitorId;
            })) ||
            {};
        let lastReadMessageTimestamp = 0;
        let lastDeliveredMessageTimestamp = 0;
        otherParticipants.forEach(participantData => {
            if (participantData.lastDeliveredMessageTimestamp > lastDeliveredMessageTimestamp) {
                lastDeliveredMessageTimestamp = participantData.lastDeliveredMessageTimestamp;
            }
            if (participantData.lastReadMessageTimestamp > lastReadMessageTimestamp) {
                lastReadMessageTimestamp = participantData.lastReadMessageTimestamp;
            }
        });
        const chatActionsMenuItems = this.chatActions
            .map((item, index) => {
            if (item.hideAction && item.hideAction(selectedChatMeta)) {
                return null;
            }
            return (<div className={styles['list-item']} key={index} onClick={() => {
                item.handleActionClick(selectedChatMeta);
            }}>
						<div className={styles['list-item-icon-container']}>
							<img src={item.icon}/>
						</div>
						<div className={styles['list-item-text']}>{item.text}</div>
					</div>);
        })
            .filter(item => !!item);
        return (<FileUploadProvider onFileUploadSuccess={this.handleFileUploadSuccess} onFilesAdd={this.onFilesAdd} getValidSelectedFiles={this.getValidSelectedFiles} autoUpload oneShot maxFilesAllowed={4} render={({ handleAddFiles, files, retryUpload }) => {
            return (<React.Fragment>
							<div className={styles['chat']}>
								<div className={styles['container']}>
									<div className={cx(styles['chat-list'], { [styles['chat-list-no-chats']]: noChatsFound })}>
										<div className={styles['chat-list-title']}>{<StringWidget strKey="chatAllChatsTitle"/>}</div>
										{!noChatsFound ? (<div className={styles['chat-list-items']}>
												{chatMetas.map((chatMeta, index) => {
                const { displayName, imageUrl, subject } = chatMeta;
                let snippet;
                const latestChatMessageData = (latestChatMessageIdentifiers || []).find(item => item.chatId === chatMeta.chatId) || {};
                const lastReadMessageData = (lastReadMessageIdentifiers || []).find(item => item.chatId === chatMeta.chatId) || {};
                if (latestChatMessageData.body) {
                    const decodedMessageBody = ab2str(new Uint8Array(latestChatMessageData.body));
                    let decodedMessage;
                    try {
                        decodedMessage = JSON.parse(decodedMessageBody);
                        snippet = decodedMessage.contents;
                    }
                    catch (error) {
                        return false;
                    }
                }
                const unreadMessageCount = lastReadMessageData.unreadCount;
                const showUnreadMessageCount = unreadMessageCount > 0;
                return (<div className={cx(styles['chat-list-item'], {
                    [styles['selected']]: this.state.selectedChat === chatMeta.chatId,
                    [styles['unread-messages']]: showUnreadMessageCount
                })} onClick={() => {
                    this.handleChatSelect(chatMeta.chatId);
                }} key={index}>
															<div className={styles['visitor-profile-picture-wrapper']}>
																<SimpleImage src={getImageUrl(imageUrl, '300', '70') || placeholderVisitorProfileIcon} containerClass={styles['visitor-profile-picture']} size={75} lazyLoadEnable/>
															</div>
															<div className={styles['chat-list-item-content']}>
																<div className={cx(styles['display-name'], styles['trucate-text'])}>{displayName}</div>
																<div className={cx(styles['subject'], styles['truncate-text'])}>{subject}</div>
																{snippet && (<div className={cx(styles['truncate-text'], styles['last-message-snippet'])}>
																		{snippet}
																	</div>)}
															</div>
															<div className={styles['time-and-message-count-container']}>
																<div className={styles['relative-time']}>
																	{getRelativeTime(latestChatMessageData.timestamp)}
																</div>
																{showUnreadMessageCount && (<div className={styles['unread-message-count']}>{unreadMessageCount}</div>)}
															</div>
														</div>);
            })}
											</div>) : (<div className={styles['no-chats']}>
												<img className={styles['no-chats-illustration']} src={noChats}/>
												<StringWidget className={styles['no-chats-title']} strKey="chatNoChatsTitle"/>
												<StringWidget className={styles['no-chats-subtitle']} strKey="chatNoChatsSubtitle"/>
											</div>)}
									</div>
									{!noChatsFound && (<div className={styles['chat-messages']}>
											<div className={styles['visitor-details']}>
												<div className={styles['visitor-profile-picture-wrapper']}>
													<SimpleImage src={getImageUrl(imageUrl, '300', '70') || placeholderVisitorProfileIcon} containerClass={styles['visitor-profile-picture']} size={75} lazyLoadEnable/>
												</div>
												<div className={styles['visitor-name']}>{displayName}</div>
												{chatActionsMenuItems.length > 0 && (<div className={styles['pop-over-menu-open-btn-container']}>
														<div className={styles['pop-over-menu-open-btn']}>
															<img src={threeDots}/>
															<Card className={styles['pop-over-menu']}>{chatActionsMenuItems}</Card>
														</div>
													</div>)}
											</div>
											<div className={styles['message-container']}>
												{this.state.selectedChat && (<ChatMessages retryUpload={retryUpload} files={files} innerRefScrollAnchor={this.innerRefScrollAnchor} chatDisabled={chatState === ChatStateEnum.ENDED || chatState === ChatStateEnum.RESOLVED} lastReadMessageTimestamp={lastReadMessageTimestamp} lastDeliveredMessageTimestamp={lastDeliveredMessageTimestamp} isVisible={this.state.isVisible} isTyping={selectedChatMeta.isTyping} messageSendCallback={this.scrollToBottom} fetchChatMessages={this.fetchChatMessages} innerRef={this.setRef} selectedChat={this.state.selectedChat} key={this.state.selectedChat} selectedChatMeta={selectedChatMeta}/>)}
												{isChatBlocked ? (<div className={styles['chat-meta-message-container']}>
														<StringWidget className={styles['chat-meta-message']} attributes={{ displayName }} strKey="chatBlockedMessage"/>
														<Button className={styles['chat-action-btn']} primary onClick={() => {
                this.updateBlockStatus(BlockStatus.UNBLOCK, selectedChatMeta);
            }}>
															{stringTemplate('chatUnblockCta')}
														</Button>
													</div>) : (this.renderMessageInput(chatState, displayName, selectedChatMeta, handleAddFiles))}
											</div>
										</div>)}
								</div>
							</div>
						</React.Fragment>);
        }}/>);
    }
}
Chat.contextTypes = {
    router: PropTypes.object
};
const mapStateToProps = state => {
    return {
        allChats: state.allChats,
        networkStatus: state.networkStatus
    };
};
const mapDispatchToProps = {};
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withToast(Chat)));
